/**
 * <swiper-init> — Custom Element that auto-initializes a native Swiper 12
 * instance on connectedCallback. Reads config from `data-swiper-settings`
 * JSON either on `this` (element itself is the Swiper root) or on the first
 * `[data-swiper-settings]` child. Optional thumb swiper via
 * `data-swiper-thumb="<selector>"` + `data-swiper-thumb-settings='<json>'`
 * on the thumb element.
 *
 * Usage in liquid:
 *   <swiper-init class="swiper" data-swiper-settings='{{ swiper_options }}'>
 *     <div class="swiper-wrapper">...</div>
 *   </swiper-init>
 *
 * Public API:
 *   element.swiper         — native Swiper 12 instance (set by Swiper itself
 *                            on `mount()`; read after `swiper:initialized`)
 *   element.thumbSwiper    — native thumb Swiper instance (if any)
 *
 * Dispatched events (bubbles):
 *   swiper:initialized  → { swiper, thumbs }
 *   swiper:destroyed
 *   swiper:slide-changed → { currentIndex, element }
 *
 * Loaded as ES module via `@theme/swiper-init` importmap entry; also loaded
 * globally from `layout/theme.liquid` so every page has the CE defined.
 *
 * Swiper itself is imported directly as an ES module — it is only fetched
 * on pages that actually use a `<swiper-init>` element (lazy load).
 */

import Swiper from '@theme/swiper';

const CENTER_INSUFFICIENT_CLASS = 'swiper--center-insufficient';
const NAVIGATION_STOP_EVENTS = ['wheel', 'touchstart', 'pointerdown'];
const easeOutCubic = (t) => 1 - (1 - t) ** 3;

class SwiperInit extends HTMLElement {
  thumbSwiper = null;
  #rootEl = null;
  #thumbEl = null;
  #navigationFrame = 0;
  #navigationWrapper = null;

  connectedCallback() {
    this.#rootEl = this.hasAttribute('data-swiper-settings')
      ? this
      : this.querySelector('[data-swiper-settings]');

    if (!this.#rootEl) {
      console.warn('<swiper-init>: no [data-swiper-settings] found', this);
      return;
    }

    const thumbSelector = this.getAttribute('data-swiper-thumb');
    if (thumbSelector) {
      this.#thumbEl = document.querySelector(thumbSelector);
    }

    this.#init();
  }

  disconnectedCallback() {
    this.#destroy();
  }

  #init() {
    let config;
    try {
      config = JSON.parse(this.#rootEl.getAttribute('data-swiper-settings'));
    } catch (e) {
      console.error('<swiper-init>: invalid data-swiper-settings JSON', e);
      return;
    }

    if (this.#thumbEl) {
      const thumbConfig = JSON.parse(
        this.#thumbEl.getAttribute('data-swiper-thumb-settings') || '{}'
      );
      this.thumbSwiper = new Swiper(this.#thumbEl, thumbConfig);
      config.thumbs = { ...(config.thumbs || {}), swiper: this.thumbSwiper };
    }

    // Swiper sets `this.#rootEl.swiper = <instance>` internally on mount().
    // When #rootEl === this (the Custom Element), that means `this.swiper`
    // becomes the native Swiper instance automatically.
    config.on = {
      ...config.on,
      slidesUpdated: (swiper) => this.#updateNativeLayout(swiper),
    };
    const swiper = new Swiper(this.#rootEl, config);

    this.#easeNativeNavigation(swiper);
    this.#revealActiveSlide(swiper);

    swiper.on('slideChange', () => {
      this.dispatchEvent(
        new CustomEvent('swiper:slide-changed', {
          bubbles: true,
          detail: { currentIndex: swiper.activeIndex, element: swiper.el },
        })
      );
    });

    this.dispatchEvent(
      new CustomEvent('swiper:initialized', {
        bubbles: true,
        detail: { swiper, thumbs: this.thumbSwiper },
      })
    );
  }

  /**
   * cssMode arrows, keyboard and pagination end in `scrollTo({ behavior: 'smooth' })`.
   * The browser's smooth scroll eases in: the row barely moved for the first frames
   * after a click, and a click during its long tail restarted that slow start, so
   * stepping card by card hesitated. Run those scrolls with an ease-out over the
   * slider's speed instead. Touch, trackpad and wheel stay native and cancel it.
   */
  #easeNativeNavigation(swiper) {
    const { params, wrapperEl } = swiper;
    if (!params.cssMode || !params.speed) return;

    const nativeScrollTo = wrapperEl.scrollTo.bind(wrapperEl);
    this.#navigationWrapper = wrapperEl;
    wrapperEl.scrollTo = (options, ...rest) => {
      this.#stopNavigation();
      if (
        options?.behavior !== 'smooth' ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return nativeScrollTo(options, ...rest);
      }

      const side = 'left' in options ? 'scrollLeft' : 'scrollTop';
      const from = wrapperEl[side];
      const to = options.left ?? options.top;
      const start = performance.now();
      // Snapping would pull every intermediate frame back to the nearest slide.
      wrapperEl.style.scrollSnapType = 'none';
      const step = (now) => {
        const progress = Math.min((now - start) / params.speed, 1);
        wrapperEl[side] = from + (to - from) * easeOutCubic(progress);
        if (progress < 1) {
          this.#navigationFrame = requestAnimationFrame(step);
        } else {
          this.#stopNavigation();
        }
      };
      this.#navigationFrame = requestAnimationFrame(step);
    };
    NAVIGATION_STOP_EVENTS.forEach((type) =>
      wrapperEl.addEventListener(type, this.#stopNavigation, { passive: true })
    );
  }

  #stopNavigation = () => {
    cancelAnimationFrame(this.#navigationFrame);
    this.#navigationFrame = 0;
    if (this.#navigationWrapper) this.#navigationWrapper.style.scrollSnapType = '';
  };

  #releaseNativeNavigation() {
    const wrapperEl = this.#navigationWrapper;
    if (!wrapperEl) return;
    this.#stopNavigation();
    NAVIGATION_STOP_EVENTS.forEach((type) =>
      wrapperEl.removeEventListener(type, this.#stopNavigation)
    );
    delete wrapperEl.scrollTo;
    this.#navigationWrapper = null;
  }

  /** Native scrolling cannot use Swiper's negative translation to center a short row. */
  #updateNativeLayout(swiper) {
    const { el, params, wrapperEl } = swiper;
    if (!params.cssMode) return;
    // Measure from the natural start, including when a resize makes a centered
    // row overflow. Centering overflowing flex items would hide their start edge.
    if (el.classList.contains(CENTER_INSUFFICIENT_CLASS)) {
      el.classList.remove(CENTER_INSUFFICIENT_CLASS);
    }
    this.#alignNativeOffsets(swiper);

    if (!params.centerInsufficientSlides) return;
    const horizontal = swiper.isHorizontal();
    const extent = horizontal ? wrapperEl.scrollWidth : wrapperEl.scrollHeight;
    const visible = horizontal ? wrapperEl.clientWidth : wrapperEl.clientHeight;
    el.classList.toggle(CENTER_INSUFFICIENT_CLASS, visible > 0 && extent <= visible);
  }

  /** Match native scroll coordinates when section padding is expressed as slide offsets. */
  #alignNativeOffsets(swiper) {
    const { params, slides, wrapperEl } = swiper;
    if (
      !params.cssMode ||
      (!params.slidesOffsetBefore && !params.slidesOffsetAfter) ||
      params.effect !== 'slide' ||
      params.centeredSlides ||
      params.loop ||
      params.virtual?.enabled ||
      params.grid?.rows > 1 ||
      params.setWrapperSize ||
      params.snapToSlideEdge ||
      slides.length !== swiper.slidesGrid.length ||
      !slides.length
    )
      return;

    // Swiper 12 subtracts slidesOffsetBefore from its transform grid, while
    // cssMode gives the first slide a positive margin. Browser snapping masks
    // that mismatch visually, but navigation/progress still use the old grid.
    const horizontal = swiper.isHorizontal();
    const rtl = horizontal && swiper.rtlTranslate;
    const viewport = wrapperEl.getBoundingClientRect();
    const scroll = horizontal ? wrapperEl.scrollLeft * (rtl ? -1 : 1) : wrapperEl.scrollTop;
    const extent = horizontal ? wrapperEl.scrollWidth : wrapperEl.scrollHeight;
    const visible = horizontal ? wrapperEl.clientWidth : wrapperEl.clientHeight;
    if (!visible) return;
    const firstStyles = getComputedStyle(slides[0]);
    const firstScrollMargin =
      parseFloat(
        horizontal ? firstStyles.scrollMarginInlineStart : firstStyles.scrollMarginBlockStart
      ) || 0;
    const rects = slides.map((slide) => slide.getBoundingClientRect());
    const positions = rects.map((rect, index) => {
      const start = horizontal
        ? rtl
          ? viewport.right - rect.right
          : rect.left - viewport.left
        : rect.top - viewport.top;
      return Math.max(0, Math.round(start + scroll - (index === 0 ? firstScrollMargin : 0)));
    });
    const lastRect = rects.at(-1);
    const lastStyles = getComputedStyle(slides.at(-1));
    const endMargin =
      parseFloat(horizontal ? lastStyles.marginInlineEnd : lastStyles.marginBlockEnd) || 0;
    const lastEdge = horizontal
      ? rtl
        ? viewport.right - lastRect.left
        : lastRect.right - viewport.left
      : lastRect.bottom - viewport.top;
    // Keep the fractional terminal edge: native RTL scrolling can round down
    // while scrollWidth rounds up, leaving isEnd false by exactly one pixel.
    const maxScroll = Math.max(
      0,
      Math.min(extent - visible, lastEdge + scroll + endMargin - visible)
    );
    const snaps = positions.filter(
      (position, index) =>
        (index - Math.min(params.slidesPerGroupSkip, index)) % params.slidesPerGroup === 0 &&
        position < maxScroll
    );
    snaps.push(maxScroll);

    swiper.slidesGrid = positions;
    swiper.snapGrid = [...new Set(snaps)];
    // Scrollbar.updateSize adds slidesOffsetBefore itself to virtualSize.
    swiper.virtualSize = extent - (Number(params.slidesOffsetBefore) || 0);
  }

  /**
   * Opt-in via `data-swiper-scroll-active="<selector>"`: after mount, jump (no
   * animation) so the slide containing the matching element is fully in view.
   * Used by nav bars (collection/search tabs) so the active tab is never left
   * off-screen or clipped when the page loads deep in the list. Skips the jump
   * when the active slide already fits — early tabs keep the natural start view.
   */
  #revealActiveSlide(swiper) {
    const activeSelector = this.getAttribute('data-swiper-scroll-active');
    if (activeSelector == null) return;

    const slide = this.#rootEl
      .querySelector(activeSelector || '.is-active')
      ?.closest('.swiper-slide');
    const index = slide ? swiper.slides.indexOf(slide) : -1;
    if (index < 0) return;

    const viewport = swiper.el.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    const fullyVisible = rect.left >= viewport.left - 1 && rect.right <= viewport.right + 1;
    if (fullyVisible) return;

    swiper.slideTo(index, 0, false);
  }

  #destroy() {
    this.#releaseNativeNavigation();
    if (this.#rootEl?.classList.contains(CENTER_INSUFFICIENT_CLASS)) {
      this.#rootEl.classList.remove(CENTER_INSUFFICIENT_CLASS);
    }
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    if (this.thumbSwiper) {
      this.thumbSwiper.destroy(true, true);
      this.thumbSwiper = null;
    }
    this.dispatchEvent(new CustomEvent('swiper:destroyed', { bubbles: true }));
  }
}

if (!customElements.get('swiper-init')) {
  customElements.define('swiper-init', SwiperInit);
}
